import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMicrophone, FaVideo } from 'react-icons/fa';
import { IoCloudUploadOutline } from 'react-icons/io5';
import { TbCalendar } from 'react-icons/tb';
import MeetingModal from './MeetingModal';
import { useRecord } from './RecordContext';
import RecordOptionsModal from './UI/RecordOptionsModel.jsx';
import configDetails from '../Config/Config';
import routesfe from '../Config/RoutesFE';
import '../Styles/TopNavbar.css';
import logger from '../utils/Logger';

const { trackingUrl, TRACKING_TIME } = configDetails;
const COMPONENT_NAME = 'TopNavbar';

const TopNavbar = ({ onToggleRightSidebar, rightSidebarVisible, authIdToken }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
   const { 
    isLive, sessionId, conversationId, timer, handleStopRef, setPreviousConversationId, audioChunksRef,
    assemblySocketRef, streamRef, audioContextRef, workletNodeRef, mediaRecorderRef
  } = useRecord();
  
  // Helper to get user_id from localStorage
  const getAuthenticatedUserId = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        return user.userSub || user.cognito_user_id;
      } catch {}
    }
    return null;
  };
  // Helper to get conversation_id from cookie
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
      // Try exact match first
      let parts = value.split(`; ${name}=`);
      if (parts.length === 2) {
        const found = parts.pop().split(';').shift();
        return found;
      }
      // If not found, try alternate key (underscore vs no underscore)
      let altName = name;
      if (name === 'conversation_id') altName = 'conversationid';
      else if (name === 'conversationid') altName = 'conversation_id';
      if (altName !== name) {
        parts = value.split(`; ${altName}=`);
        if (parts.length === 2) {
          const found = parts.pop().split(';').shift();
          return found;
        }
      }
      return null;
  };
  // Helper to check if there's an active recording
  const checkActiveRecording = () => {
    const isActive = getCookie('conversation_active') === 'true';
    const status = getCookie('recording_status');
    const cookieConversationId = getCookie('conversation_id');
    
    // Check cookie-based session
    const hasCookieSession = isActive && (status === '🎙️ Listening...' || status === 'Paused') && cookieConversationId;
    
    // Also check context-based session (isLive from RecordContext)
    const hasContextSession = isLive && (conversationId || sessionId);
    
    return hasCookieSession || hasContextSession;
  };

  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingURL, setMeetingURL] = useState('');
  const { resetAllRecordData } = useRecord();
  const [showRecordOptions, setShowRecordOptions] = useState(false);
  const [hasActiveRecording, setHasActiveRecording] = useState(false);
  const [isSavingSession, setIsSavingSession] = useState(false);

  useEffect(() => {
    const userId = getAuthenticatedUserId();
    logger.info('TopNavbar component mounted', {
      component: COMPONENT_NAME,
      action: 'COMPONENT_MOUNT',
      userId,
      context: {
        pathname: window.location.pathname,
        isLive,
        hasActiveRecording: checkActiveRecording(),
        rightSidebarVisible,
        sessionId: sessionId || getCookie('session_id') || null,
        conversationId: conversationId || getCookie('conversation_id') || null,
        browserOnline: navigator.onLine,
        timestamp: Date.now()
      },
      includeBrowserInfo: true
    });

    return () => {
      logger.info('TopNavbar component unmounted', {
        component: COMPONENT_NAME,
        action: 'COMPONENT_UNMOUNT',
        userId: getAuthenticatedUserId(),
        context: {
          isLive,
          hasActiveRecording,
          sessionId: sessionId || getCookie('session_id') || null,
          conversationId: conversationId || getCookie('conversation_id') || null
        }
      });
    };
  }, []);

  useEffect(() => {
    const checkStatus = () => {
      const hasActive = checkActiveRecording();
      setHasActiveRecording(hasActive);
    };
    checkStatus();
    // Check periodically in case cookies change
    const interval = setInterval(checkStatus, 1000);
    return () => clearInterval(interval);
  }, [isLive]);

  const handleImportClick = () => {
    logger.info('Import button clicked', {
      component: COMPONENT_NAME,
      action: 'CLICK_IMPORT',
      userId: getAuthenticatedUserId(),
      context: {
        pathname: window.location.pathname,
        timestamp: Date.now()
      }
    });
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      logger.info('File selected for import', {
        component: COMPONENT_NAME,
        action: 'FILE_SELECTED',
        userId: getAuthenticatedUserId(),
        context: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          timestamp: Date.now()
        }
      });
    }
  };

  const handleJoinMeeting = () => {
    logger.info('Join meeting action triggered', {
      component: COMPONENT_NAME,
      action: 'CLICK_JOIN_MEETING',
      userId: getAuthenticatedUserId(),
      context: {
        hasMeetingUrl: meetingURL.trim() !== '',
        timestamp: Date.now()
      }
    });
    if (meetingURL.trim() !== '') {
      window.open(meetingURL.trim(), '_blank');
    }
    setShowMeetingModal(false);
  };

  // Record button click logic
const handleRecordClick = () => {
  const userId = getAuthenticatedUserId();
  const cookieConversationId = getCookie('conversation_id');
  const isActive = getCookie('conversation_active') === 'true';
  const recordingStatus = getCookie('recording_status');
  
  // Check both cookie-based session and context-based session
  const hasCookieSession = isActive && cookieConversationId && 
    (recordingStatus === '🎙️ Listening...' || recordingStatus === 'Paused');
  
  // Also check if isLive from RecordContext is true with a valid conversationId
  const hasContextSession = isLive && (conversationId || sessionId);
  
  // Use either source to determine if there's a valid session
  const hasValidSession = hasCookieSession || hasContextSession;
  const effectiveConversationId = cookieConversationId || conversationId;
  
  logger.info('Record button clicked', {
    component: COMPONENT_NAME,
    action: 'CLICK_RECORD',
    userId,
    context: {
      pathname: window.location.pathname,
      hasValidSession,
      hasCookieSession,
      hasContextSession,
      isActive,
      cookieConversationId: cookieConversationId || null,
      contextConversationId: conversationId || null,
      recordingStatus: recordingStatus || null,
      isLive,
      sessionId: sessionId || null,
      timer,
      timestamp: Date.now()
    }
  });
  
  if (hasValidSession) {
    logger.debug('Active session found, showing record options', {
      component: COMPONENT_NAME,
      action: 'SHOW_RECORD_OPTIONS',
      userId,
      context: { 
        effectiveConversationId, 
        recordingStatus,
        source: hasCookieSession ? 'cookie' : 'context'
      }
    });
    if (effectiveConversationId) {
      setPreviousConversationId(effectiveConversationId);
    }
    setHasActiveRecording(true);
    setShowRecordOptions(true);
  } else {
    logger.info('No active session, clearing stale state and navigating to Record page', {
      component: COMPONENT_NAME,
      action: 'NAVIGATE_TO_RECORD',
      userId,
      context: {
        reason: 'no_active_session',
        pathname: window.location.pathname,
        timestamp: Date.now()
      }
    });
    setHasActiveRecording(false);
    
    // Clear any stale cookies that might show old timer/state or hide start button
    const staleCookies = [
      'recording_start_time', 'recording_status', 'is_recording_paused',
      'pause_start_time', 'total_pause_duration', 'last_activity_time',
      'original_session_id', 'conversation_id', 'conversation_active',
      'session_id', 'isLiveSession', 'liveSessionId',
      'transcription_token', 'token_issued_at', 'token_expires_at', 'token_status'
    ];
    staleCookies.forEach(name => {
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
    
    // Also clear localStorage items
    localStorage.removeItem('session_id');
    localStorage.removeItem('transcript_segments');
    
    // Reset context state
    if (typeof resetAllRecordData === 'function') {
      resetAllRecordData();
    }
    
    // Navigate to Record page in ready state (user will click Start to begin)
    navigate(routesfe.RECORD);
  }
};


  // Handle ongoing recording - navigate to existing session
  const handleOngoing = () => {
    const userId = getAuthenticatedUserId();
    const cookieConversationId = getCookie('conversation_id');
    // Use cookie or context conversationId
    const effectiveConversationId = cookieConversationId || conversationId;
    
    logger.info('User chose to continue ongoing recording', {
      component: COMPONENT_NAME,
      action: 'CLICK_ONGOING_RECORDING',
      userId,
      context: {
        cookieConversationId: cookieConversationId || null,
        contextConversationId: conversationId || null,
        effectiveConversationId: effectiveConversationId || null,
        isLive,
        pathname: window.location.pathname,
        timestamp: Date.now()
      }
    });
    
    if (!effectiveConversationId) {
      logger.warn('No conversation ID found for ongoing session', {
        component: COMPONENT_NAME,
        action: 'ONGOING_SESSION_NOT_FOUND',
        userId,
        context: {
          reason: 'missing_conversation_id',
          isLive,
          timestamp: Date.now()
        }
      });
      setShowRecordOptions(false);
      setHasActiveRecording(false);
      navigate(routesfe.RECORD);
      return;
    }
    setShowRecordOptions(false);
    sessionStorage.setItem('resume_ongoing_session', 'true');
    navigate(`/record/${effectiveConversationId}`);
  };

// Handle new recording - stop old session and start fresh
const handleNewRecording = async () => {
  const userId = getAuthenticatedUserId();
  const cookieConversationId = getCookie('conversation_id');
  const currentSessionId = sessionId || getCookie('session_id');
  
  logger.info('User chose to start new recording', {
    component: COMPONENT_NAME,
    action: 'CLICK_NEW_RECORDING',
    userId,
    context: {
      existingConversationId: cookieConversationId || null,
      existingSessionId: currentSessionId || null,
      isLive,
      timer,
      pathname: window.location.pathname,
      timestamp: Date.now()
    }
  });
  
  setIsSavingSession(true);
  window.isStoppingSession = true;
  try {
    const effectiveConversationId = conversationId || cookieConversationId || getCookie('conversation_id') || null;
    
    if (effectiveConversationId) {
      setPreviousConversationId(effectiveConversationId);
    }

    logger.debug('Stopping audio streams and WebSocket connections', {
      component: COMPONENT_NAME,
      action: 'STOP_AUDIO_STREAMS',
      userId,
      context: {
        effectiveConversationId,
        currentSessionId,
        timestamp: Date.now()
      }
    });
    if (assemblySocketRef?.current) {
      try {
        assemblySocketRef.current.close();
        assemblySocketRef.current = null;
      } catch (e) {
        console.error('[TopNavbar] Error closing AssemblyAI socket:', e);
      }
    }
    if (window.assemblySocketRef?.current) {
      try {
        window.assemblySocketRef.current.close();
        window.assemblySocketRef.current = null;
      } catch (e) {}
    }
    
    // Stop the audio worklet node
    if (workletNodeRef?.current) {
      try {
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      } catch (e) {}
    }
    if (window.workletNodeRef?.current) {
      try {
        window.workletNodeRef.current.disconnect();
        window.workletNodeRef.current = null;
      } catch (e) {}
    }
    
    // Stop the microphone stream immediately
    if (streamRef?.current) {
      try {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      } catch (e) {}
    }
    if (window.streamRef?.current) {
      try {
        window.streamRef.current.getTracks().forEach(track => track.stop());
        window.streamRef.current = null;
      } catch (e) {}
    }
    
    // Close audio context
    if (audioContextRef?.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
        audioContextRef.current = null;
      } catch (e) {}
    }
    if (window.audioContextRef?.current) {
      try {
        if (window.audioContextRef.current.state !== 'closed') {
          window.audioContextRef.current.close();
        }
        window.audioContextRef.current = null;
      } catch (e) {}
    }
    
    // Stop media recorder if running
    if (mediaRecorderRef?.current) {
      try {
        if (mediaRecorderRef.current.state === 'recording' || mediaRecorderRef.current.state === 'paused') {
          mediaRecorderRef.current.stop();
        }
      } catch (e) {}
    }
    if (window.mediaRecorderRef?.current) {
      try {
        if (window.mediaRecorderRef.current.state === 'recording' || window.mediaRecorderRef.current.state === 'paused') {
          window.mediaRecorderRef.current.stop();
        }
      } catch (e) {}
    }

    // Helper function to send STOPPED status via WebSocket
    const sendStoppedStatus = async (socket) => {
      // Send conversation_live_status to update backend status
      const statusPayload = {
        action: 'conversation_live_status',
        conversation_id: cookieConversationId,
        user_id: userId,
        current_status: 'STOPPED',
        time: timer || 0
      };
      socket.send(JSON.stringify(statusPayload));
      
      // Send segment with is_stop: true to notify viewers (this is what viewers listen for)
      const segmentPayload = {
        action: 'segment',
        conversation_id: cookieConversationId,
        text: '',
        is_partial: false,
        is_pause: false,
        is_stop: true,
        div_id: null,
        sequence_id: null
      };
      socket.send(JSON.stringify(segmentPayload));
      
      // Send STOPPED status again after a short delay to ensure viewers receive it
      await new Promise(resolve => setTimeout(resolve, 100));
      socket.send(JSON.stringify(statusPayload));
      socket.send(JSON.stringify(segmentPayload));
      
      // Wait for the messages to be processed by backend
      await new Promise(resolve => setTimeout(resolve, 200));
    };

    // Try to send STOPPED status via main socket first
    let stoppedSent = false;
    if (window.socketRef?.current && window.socketRef.current.readyState === WebSocket.OPEN) {
      try {
        await sendStoppedStatus(window.socketRef.current);
        stoppedSent = true;
      } catch (error) {
        console.error('❌ [TopNavbar] Error sending STOPPED via main socket:', error);
      }
    }

    // If main socket not available, create a new WebSocket to send STOPPED
    if (!stoppedSent && cookieConversationId && userId) {
      try {
        const tokenPayload = {
          user_id: userId,
          session_id: currentSessionId || cookieConversationId,
          conversation_id: cookieConversationId,
          user_type: "speaker",
          connection_source: "web",
        };
        const token = btoa(JSON.stringify(tokenPayload));
        const wsUrl = `${configDetails.websocketBaseUrl}?token=${token}`;
        
        const tempSocket = new WebSocket(wsUrl);
        
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            tempSocket.close();
            reject(new Error('WebSocket connection timeout'));
          }, 10000);
          
          tempSocket.onopen = async () => {
            clearTimeout(timeout);
            
            try {
              await sendStoppedStatus(tempSocket);
              // Wait a bit more for backend to broadcast to viewers
              await new Promise(r => setTimeout(r, 300));
            } catch (err) {
              console.error('❌ [TopNavbar] Error sending STOPPED:', err);
            }
            
            tempSocket.close();
            resolve(true);
          };
          
          tempSocket.onerror = (error) => {
            clearTimeout(timeout);
            tempSocket.close();
            reject(error);
          };
        });
      } catch (error) {
        console.error('❌ [TopNavbar] Error creating WebSocket for STOPPED:', error);
      }
    }
    
    // Broadcast STOPPED event locally for LiveTranscript
    window.dispatchEvent(new CustomEvent('recording-stopped', {
      detail: {
        conversation_id: cookieConversationId,
        user_id: userId,
        time: timer || 0
      }
    }));

    // Notify viewers immediately (same as Record page)
    if (typeof window.sendSegmentToViewers === 'function') {
      const currentDivId = window.currentDivIdRef?.current || null;
      const sequenceMap = window.sequenceMapRef?.current || {};
      const sequenceId = currentDivId ? sequenceMap[currentDivId] || 0 : 0;
      const getFormattedTimestamp = date => date.toISOString().slice(0, 19);
      window.sendSegmentToViewers('', false, 'stop', currentDivId, sequenceId, getFormattedTimestamp(new Date()));
    }

    // Capture audio chunks before stopping
    let capturedAudioChunks = [];
    
    if (audioChunksRef?.current && audioChunksRef.current.length > 0) {
      capturedAudioChunks = [...audioChunksRef.current];
    } else if (window.mediaRecorderRef?.audioChunks) {
      capturedAudioChunks = [...window.mediaRecorderRef.audioChunks];
    }

    if (userId && cookieConversationId && capturedAudioChunks.length > 0) {
      try {
        const audioBlob = new Blob(capturedAudioChunks, { type: 'audio/webm' });
        if (audioBlob.size > 0) {
          logger.info('Uploading audio recording', {
            component: COMPONENT_NAME,
            action: 'AUDIO_UPLOAD_START',
            userId,
            context: {
              conversationId: cookieConversationId,
              audioBlobSize: audioBlob.size,
              audioChunksCount: capturedAudioChunks.length,
              timestamp: Date.now()
            }
          });
          
          const payload = {
            user_id: userId,
            conversation_id: cookieConversationId,
          };

          const response = await fetch(`${configDetails.baseUrl}${configDetails.UPLOAD_AUDIO}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            const { presigned_url } = await response.json();
            const uploadResponse = await fetch(presigned_url, {
              method: 'PUT',
              headers: { 'Content-Type': 'audio/webm' },
              body: audioBlob,
            });
            
            logger.info('Audio upload completed', {
              component: COMPONENT_NAME,
              action: 'AUDIO_UPLOAD_SUCCESS',
              userId,
              context: {
                conversationId: cookieConversationId,
                uploadStatus: uploadResponse.status,
                timestamp: Date.now()
              }
            });
          } else {
            logger.error('Failed to get presigned URL for audio upload', {
              component: COMPONENT_NAME,
              action: 'AUDIO_UPLOAD_PRESIGNED_FAILED',
              userId,
              context: {
                conversationId: cookieConversationId,
                responseStatus: response.status,
                timestamp: Date.now()
              }
            });
          }
        }
      } catch (error) {
        logger.error('Audio upload error', {
          component: COMPONENT_NAME,
          action: 'AUDIO_UPLOAD_ERROR',
          userId,
          error,
          context: {
            conversationId: cookieConversationId,
            timestamp: Date.now()
          }
        });
      }
    }

    // STEP 3: Send tracking_time API
    const minutes = ((timer || 0) / 60).toFixed(2);
    try {
      logger.info('Sending tracking time', {
        component: COMPONENT_NAME,
        action: 'TRACKING_TIME_SEND',
        userId,
        context: {
          minutes: parseFloat(minutes),
          timerSeconds: timer || 0,
          timestamp: Date.now()
        }
      });
      
      await fetch(`${trackingUrl}${TRACKING_TIME}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
        body: JSON.stringify({ user_id: userId, time: parseFloat(minutes) })
      });
      // Dispatch event to refresh tracking time in sidebar
      window.dispatchEvent(new Event('refresh-tracking-time'));
      
      logger.debug('Tracking time sent successfully', {
        component: COMPONENT_NAME,
        action: 'TRACKING_TIME_SUCCESS',
        userId,
        context: { minutes: parseFloat(minutes) }
      });
    } catch (err) {
      logger.error('Error sending tracking time', {
        component: COMPONENT_NAME,
        action: 'TRACKING_TIME_ERROR',
        userId,
        error: err,
        context: { minutes: parseFloat(minutes), timestamp: Date.now() }
      });
    }
    
    if (currentSessionId && userId && effectiveConversationId) {
      const sessionCompletePayload = {
        user_id: userId,
        session_id: currentSessionId,
        conversation_id: effectiveConversationId,
        path: `${userId}/${currentSessionId}.webm`,
        time_stamp: new Date().toISOString(),
        seconds_used: timer || 0,
      };

      logger.info('Sending session completed API', {
        component: COMPONENT_NAME,
        action: 'SESSION_COMPLETE_SEND',
        userId,
        context: {
          sessionId: currentSessionId,
          conversationId: effectiveConversationId,
          secondsUsed: timer || 0,
          timestamp: Date.now()
        }
      });

      try {
        const response = await fetch(`${configDetails.baseUrl}${configDetails.CONVERSATION_COMPLETED}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
          body: JSON.stringify(sessionCompletePayload),
        });
        
        if (response.ok) {
          logger.info('Session completed successfully', {
            component: COMPONENT_NAME,
            action: 'SESSION_COMPLETE_SUCCESS',
            userId,
            context: {
              sessionId: currentSessionId,
              conversationId: effectiveConversationId,
              timestamp: Date.now()
            }
          });
        } else {
          logger.error('Session complete API returned error', {
            component: COMPONENT_NAME,
            action: 'SESSION_COMPLETE_FAILED',
            userId,
            context: {
              sessionId: currentSessionId,
              conversationId: effectiveConversationId,
              responseStatus: response.status,
              timestamp: Date.now()
            }
          });
        }
      } catch (error) {
        logger.error('Session complete API error', {
          component: COMPONENT_NAME,
          action: 'SESSION_COMPLETE_ERROR',
          userId,
          error,
          context: {
            sessionId: currentSessionId,
            conversationId: effectiveConversationId,
            timestamp: Date.now()
          }
        });
      }
    }

    // STEP 5: Clear all session cookies
    const sessionCookies = [
      'recording_start_time',
      'recording_status',
      'is_recording_paused',
      'pause_start_time',
      'total_pause_duration',
      'last_activity_time',
      'original_session_id',
      'conversation_id',
      'conversation_active',
      'session_id',
      'transcription_token',
      'token_issued_at',
      'token_expires_at',
      'token_status',
    ];

    sessionCookies.forEach((cookieName) => {
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });

    // STEP 6: Close modal and navigate
    setShowRecordOptions(false);
    
    // Small delay to ensure cookies are cleared
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // STEP 7: Signal new session should start
    sessionStorage.setItem('start_new_session', 'true');
    
    // Clear the stopping flag before navigating so new session can start
    window.isStoppingSession = false;
    
    logger.info('New recording session initiated, navigating to Record page', {
      component: COMPONENT_NAME,
      action: 'NEW_SESSION_NAVIGATE',
      userId,
      context: {
        previousConversationId: effectiveConversationId,
        previousSessionId: currentSessionId,
        pathname: routesfe.RECORD,
        timestamp: Date.now()
      }
    });
    
    navigate(routesfe.RECORD);
    
  } catch (err) {
    logger.error('Error in handleNewRecording', {
      component: COMPONENT_NAME,
      action: 'NEW_RECORDING_ERROR',
      userId: getAuthenticatedUserId(),
      error: err,
      context: {
        timestamp: Date.now()
      }
    });
    window.isStoppingSession = false;
    setShowRecordOptions(false);
    navigate(routesfe.RECORD);
    } finally {
      setIsSavingSession(false);
  }
};


  return (
    <>
      {/* Elegant Light Theme Loading Overlay */}
      {isSavingSession && (
        <div className="loading-overlay">
          {/* Icon Container with Gradient Background */}
          <div className="loading-icon-container">
            {/* Inner White Circle */}
            <div className="loading-inner-circle">
              <FaMicrophone className="loading-microphone-icon" />
            </div>
            
            {/* Animated Ring */}
            <div className="loading-animated-ring"></div>
          </div>

          {/* Text Content */}
          <div className="loading-text-container">
            <h2 className="loading-title">
              Saving Your Recording
            </h2>
            <p className="loading-description">
              Please wait while Noteforge securely saves your current session
            </p>
            <p className="loading-subdescription">
              Your new recording will start automatically
            </p>
          </div>

          {/* Progress Dots */}
          <div className="loading-progress-dots">
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
            <div className="loading-dot"></div>
          </div>
        </div>
      )}

     <nav 
        className="top-navbar navbar navbar-light"
      >
        {/* Right side icons/buttons */}
        <div className="navbar-buttons-container"> 
          <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }}/>

          {/* **********Record Button********** */}
          <button
            onClick={handleRecordClick} 
            className="navbar-btn navbar-btn-record">
            <FaMicrophone className="me-2" size={18} />
            Record
          </button>
          {/* **********RightSidebar Toggle — only visible when sidebar is closed********** */}
          {onToggleRightSidebar && !rightSidebarVisible && (
            <button
              onClick={() => {
                logger.info('Sidebar toggle clicked', {
                  component: COMPONENT_NAME,
                  action: 'CLICK_SIDEBAR_TOGGLE',
                  userId: getAuthenticatedUserId(),
                  context: {
                    currentState: 'hidden',
                    newState: 'visible',
                    pathname: window.location.pathname,
                    timestamp: Date.now()
                  }
                });
                onToggleRightSidebar();
              }}
              className="navbar-btn navbar-btn-sidebar inactive"
              title="Show calendar"
            >
              <TbCalendar size={22} />
            </button>
          )}
        </div>
      </nav>

      {/* **********Modal rendered here********* */}
      <MeetingModal
        show={showMeetingModal}
        onHide={() => setShowMeetingModal(false)}
        meetingURL={meetingURL}
        setMeetingURL={setMeetingURL}
        onJoin={handleJoinMeeting}
      />
      {/* Record Options Modal for ongoing session */}
      <RecordOptionsModal
        show={showRecordOptions}
        onClose={() => setShowRecordOptions(false)}
        onOngoing={handleOngoing}
        onNew={handleNewRecording}
        ongoingSessionExists={isLive || hasActiveRecording}
      />
    </>
  );
};

export default TopNavbar;